// Detail pane for a selected SignupRequest.
// Spec: docs/spec/core/part2-product-requirements.md §13.3 Reqs 616-620.
// Spec: docs/spec/topics/onboarding/approval-queue.md "Right pane — detail".

import { useState } from 'react';
import { trpc } from '../../api/trpc';

const REJECTION_REASONS = [
  { value: 'out_of_icp', label: 'Out of ICP' },
  { value: 'capacity', label: 'At capacity' },
  { value: 'suspicious', label: 'Suspicious / abuse signals' },
  { value: 'personal_domain', label: 'Personal domain (no company context)' },
  { value: 'duplicate', label: 'Duplicate signup' },
  { value: 'other', label: 'Other (internal note required)' },
] as const;

type RejectionReason = (typeof REJECTION_REASONS)[number]['value'];

interface Props {
  requestId: string;
  sessionToken: string;
  onActionComplete: () => void;
}

export function SignupRequestDetail({ requestId, sessionToken, onActionComplete }: Props) {
  const detailQuery = trpc.auth.admin.signupRequests.get.useQuery({
    sessionToken,
    requestId,
  });

  const [actionPanel, setActionPanel] = useState<'none' | 'approve' | 'reject'>('none');
  const [personalMessage, setPersonalMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState<RejectionReason>('out_of_icp');
  const [internalNote, setInternalNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const approveMutation = trpc.auth.admin.signupRequests.approve.useMutation({
    onSuccess: () => {
      setActionPanel('none');
      setPersonalMessage('');
      onActionComplete();
    },
    onError: (err) => setActionError(err.message),
  });

  const rejectMutation = trpc.auth.admin.signupRequests.reject.useMutation({
    onSuccess: () => {
      setActionPanel('none');
      setInternalNote('');
      onActionComplete();
    },
    onError: (err) => setActionError(err.message),
  });

  const resendMutation = trpc.auth.admin.signupRequests.resendApprovalEmail.useMutation({
    onSuccess: () => onActionComplete(),
    onError: (err) => setActionError(err.message),
  });

  if (detailQuery.isLoading) {
    return <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div>;
  }
  if (detailQuery.error) {
    return (
      <div style={{ color: 'var(--color-text-error, #c33)' }}>
        {detailQuery.error.message}
      </div>
    );
  }
  if (!detailQuery.data) return null;

  const sr = detailQuery.data;

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>{sr.company || '(personal email)'}</h1>
      <div style={{ color: 'var(--color-text-muted, #888)', fontSize: 14 }}>
        Status: <strong>{sr.status}</strong>
      </div>

      {actionError && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: 'var(--sev-crit-bg)',
            border: '1px solid var(--sev-crit-border)',
            borderRadius: 6,
            color: 'var(--color-error)',
          }}
        >
          {actionError}
        </div>
      )}

      {/* Submitted-by section */}
      <section style={sectionStyle()}>
        <h2 style={sectionTitleStyle()}>Submitted by</h2>
        <DataRow label="Name" value={sr.name ?? '—'} />
        <DataRow label="Email" value={sr.email} />
        <DataRow label="Role" value={sr.role ?? '—'} />
        <DataRow label="Submitted" value={new Date(sr.createdAt).toLocaleString()} />
      </section>

      {/* Use case */}
      {sr.context && (
        <section style={sectionStyle()}>
          <h2 style={sectionTitleStyle()}>Use case</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{sr.context}</p>
        </section>
      )}

      {/* Domain check */}
      <section style={sectionStyle()}>
        <h2 style={sectionTitleStyle()}>Domain check</h2>
        <DataRow
          label="Domain type"
          value={sr.domainCheck.isPersonalDomain ? 'Personal' : 'Corporate'}
        />
        <DataRow
          label="Prior signups from this domain"
          value={String(sr.domainCheck.priorSignupsFromDomain)}
        />
        <DataRow
          label="Captcha"
          value={sr.domainCheck.captchaPassed ? 'Passed' : 'Failed'}
        />
      </section>

      {/* Audit trail */}
      <section style={sectionStyle()}>
        <h2 style={sectionTitleStyle()}>Audit trail</h2>
        {sr.auditTrail.length === 0 ? (
          <div style={{ color: 'var(--color-text-muted)' }}>No events yet.</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {sr.auditTrail.map((ev: {
              id: string;
              eventType: string;
              actorEmail: string | null;
              createdAt: Date | string;
            }) => (
              <li key={ev.id} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--color-border, #2a2a2a)' }}>
                <span style={{ fontFamily: 'monospace' }}>{ev.eventType}</span>{' '}
                <span style={{ color: 'var(--color-text-muted)' }}>
                  · {new Date(ev.createdAt).toLocaleString()}
                  {ev.actorEmail ? ` · ${ev.actorEmail}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Action buttons (status-aware) */}
      {sr.status === 'pending' && actionPanel === 'none' && (
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={() => { setActionError(null); setActionPanel('approve'); }}
            style={primaryButtonStyle()}
          >
            Approve and send invite
          </button>
          <button
            onClick={() => { setActionError(null); setActionPanel('reject'); }}
            style={secondaryButtonStyle()}
          >
            Reject
          </button>
        </div>
      )}

      {sr.status === 'approved' && (
        <div style={{ marginTop: 24 }}>
          <button
            onClick={() => {
              setActionError(null);
              resendMutation.mutate({ sessionToken, requestId });
            }}
            disabled={resendMutation.isPending}
            style={secondaryButtonStyle()}
          >
            {resendMutation.isPending ? 'Resending…' : 'Resend approval email'}
          </button>
        </div>
      )}

      {/* Approve panel */}
      {actionPanel === 'approve' && (
        <section style={panelStyle()}>
          <h2 style={sectionTitleStyle()}>Approve</h2>
          <p style={{ color: 'var(--color-text-muted)' }}>
            This will create a new tenant for <strong>{sr.company || sr.emailDomain}</strong> and
            send <strong>{sr.email}</strong> an invitation link valid for 7 days.
          </p>
          <label style={{ display: 'block', marginTop: 12 }}>
            <span style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
              Optional personal message (rendered verbatim in the approval email):
            </span>
            <textarea
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={4}
              style={textareaStyle()}
            />
          </label>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={() => {
                setActionError(null);
                approveMutation.mutate({
                  sessionToken,
                  requestId,
                  personalMessage: personalMessage || undefined,
                });
              }}
              disabled={approveMutation.isPending}
              style={primaryButtonStyle()}
            >
              {approveMutation.isPending ? 'Approving…' : 'Send approval email'}
            </button>
            <button onClick={() => setActionPanel('none')} style={secondaryButtonStyle()}>
              Cancel
            </button>
          </div>
        </section>
      )}

      {/* Reject panel */}
      {actionPanel === 'reject' && (
        <section style={panelStyle()}>
          <h2 style={sectionTitleStyle()}>Reject</h2>
          <p style={{ color: 'var(--color-text-muted)' }}>
            This will send <strong>{sr.email}</strong> a polite decline email.
            The reason is internal-only and will not appear in the email body.
          </p>
          <label style={{ display: 'block', marginTop: 12 }}>
            <span style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Reason</span>
            <select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value as RejectionReason)}
              style={selectStyle()}
            >
              {REJECTION_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'block', marginTop: 12 }}>
            <span style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
              Internal note {rejectionReason === 'other' && '(required for "other")'}
            </span>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              rows={3}
              style={textareaStyle()}
            />
          </label>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={() => {
                setActionError(null);
                rejectMutation.mutate({
                  sessionToken,
                  requestId,
                  reason: rejectionReason,
                  internalNote: internalNote || undefined,
                });
              }}
              disabled={rejectMutation.isPending}
              style={primaryButtonStyle()}
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
            </button>
            <button onClick={() => setActionPanel('none')} style={secondaryButtonStyle()}>
              Cancel
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Styling helpers (inline to avoid CSS-module sprawl in Tier 3) ──────

function sectionStyle(): React.CSSProperties {
  return { marginTop: 24 };
}

function sectionTitleStyle(): React.CSSProperties {
  return { fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--color-text-muted, #888)', marginBottom: 8 };
}

function panelStyle(): React.CSSProperties {
  return {
    marginTop: 24,
    padding: 16,
    border: '1px solid var(--color-border, #2a2a2a)',
    borderRadius: 8,
  };
}

function primaryButtonStyle(): React.CSSProperties {
  return {
    padding: '8px 16px',
    background: 'var(--color-accent)',
    color: 'var(--color-text-on-accent)',
    border: '1px solid var(--color-accent)',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
  };
}

function secondaryButtonStyle(): React.CSSProperties {
  return {
    padding: '8px 16px',
    background: 'transparent',
    color: 'inherit',
    border: '1px solid var(--color-border, #2a2a2a)',
    borderRadius: 6,
    cursor: 'pointer',
  };
}

function textareaStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: 8,
    background: 'var(--color-bg-input, #0a0a0a)',
    color: 'inherit',
    border: '1px solid var(--color-border, #2a2a2a)',
    borderRadius: 6,
    fontFamily: 'inherit',
  };
}

function selectStyle(): React.CSSProperties {
  return {
    padding: '6px 8px',
    background: 'var(--color-bg-input, #0a0a0a)',
    color: 'inherit',
    border: '1px solid var(--color-border, #2a2a2a)',
    borderRadius: 6,
  };
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid var(--color-border, #2a2a2a)' }}>
      <span style={{ flex: '0 0 200px', color: 'var(--color-text-muted, #888)', fontSize: 13 }}>{label}</span>
      <span style={{ flex: 1, fontSize: 14 }}>{value}</span>
    </div>
  );
}
